import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Buttons, Icon, IconButton } from '~/components';
import { useApi, useToast, useWorkspace, useSession } from '~/contexts';
import { colors, weights } from '~/styles';
import SyncStatusBar, { SyncProgress, SyncStatus, estimateRange, formatRange } from './SyncStatusBar';

const REFRESH_SYNC_STATUS_SECONDS = 2;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderStatus = styled.div`
  display: flex;
  align-items: center;
`;

const LastActivity = styled.div`
  margin-right: 0.75rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
`;

const Estimate = styled.div`
  color: ${colors.grey55};
  font-size: 0.875rem;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  font-weight: ${weights.light};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 1.5rem;
`;

const Notice = styled.div`
  display: flex;
  flex: 1;
  min-height: 4.75rem;
  margin-right: 1.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  background-color: ${colors.warning10};
  border-left: 0.1875rem solid ${colors.warning};
  border-radius: 0.3125rem;
`;

const NoticeBlock = styled(Notice)`
  margin-top: 1rem;
  margin-right: 0;
`;

const NoticeIcon = styled(Icon)`
  margin-right: 0.75rem;
  color: ${colors.warning};
`;

const NoticeText = styled.div`
  strong {
    display: block;
  }
`;

const Intro = styled.div`
  display: flex;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  background-color: ${colors.grey5};
  border-radius: 0.3125rem;

  p:not(:last-child) {
    margin-bottom: 0.5rem;
  }
`;

const IntroIcon = styled(Icon)`
  margin-right: 0.75rem;
  color: ${colors.grey55};
`;

const TilesLabel = styled.div`
  margin: 1rem 0 0.5rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
`;

const Tile = styled.div`
  padding: 0.5rem 0.75rem;
  background-color: ${colors.grey5};
  border-radius: 0.3125rem;
`;

const TileLabel = styled.div`
  color: ${colors.grey55};
  font-size: 0.75rem;
`;

const TileValue = styled.div`
  font-size: 1.25rem;
  font-weight: ${weights.bold};
  white-space: nowrap;
`;

export default function LatestSync({
  integration,
  refreshDate,
  results,
  estimate,
  onNewSync,
  onSync,
  onChangeSettings,
}) {
  const api = useApi();
  const toast = useToast();
  const { workspace } = useWorkspace();
  const { isAdmin } = useSession();
  const [sync, setSync] = useState(null);

  const fetchSync = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).harvest.getSyncs();
      const sortedSyncs = _.sortBy(data, 'createdAt');
      const latestSync = sortedSyncs[sortedSyncs.length - 1] || null;
      setSync(latestSync);
      onSync(latestSync);
    } catch (error) {
      toast.error('There was an error fetching the migration status');
    }
  }, [api, toast, workspace, onSync]);

  useEffect(() => {
    fetchSync();
    return fetchSync.cancel;
  }, [fetchSync, refreshDate]);

  useEffect(() => {
    let interval = null;
    if (sync?.isRunning) {
      interval = setInterval(fetchSync, REFRESH_SYNC_STATUS_SECONDS * 1000);
    }
    return () => {
      if (interval) {
        clearTimeout(interval);
      }
    };
  }, [sync?.isRunning, fetchSync]);

  const isRunning = Boolean(sync?.isRunning);
  const isNeverRun = !sync || sync.statusId === 'never-run';
  const harvestMembers = results?.find((result) => result.name === 'Members')?.totalHarvest;
  const harvestClients = results?.find((result) => result.name === 'Clients')?.totalHarvest;
  const harvestProjects = results?.find((result) => result.name === 'Projects')?.totalHarvest;
  const harvestTimeEntries = results?.find((result) => result.name === 'Time Entries')?.totalHarvest;
  const harvestInvoices = results?.find((result) => result.name === 'Invoices')?.totalHarvest;
  const guidedMigration =
    !isAdmin &&
    ((harvestMembers ?? 0) > integration.settings.guidedMigrationMembers ||
      (harvestTimeEntries ?? 0) > integration.settings.guidedMigrationTimeEntries ||
      (harvestInvoices ?? 0) > integration.settings.guidedMigrationInvoices);
  const range = estimate ? estimateRange(estimate.estimatedSeconds) : null;
  const runDisabled = !results || guidedMigration;
  const startLabel = integration.settings.canRunIncremental ? 'Update' : 'Start Migration';
  const migratedWithoutSync = isNeverRun && integration.settings.hasMigratedData;
  const showEstimate = isNeverRun && !migratedWithoutSync && !guidedMigration && Boolean(results) && Boolean(range);
  const recordCount = estimate?.recordCount;
  const records = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(
    recordCount ?? 0,
  );
  const duration = range ? formatRange(range.low, range.high) : '';

  const notices = {
    loading: (
      <>
        <NoticeIcon icon="spinner" />
        <NoticeText>
          <strong>Checking your Harvest account…</strong> We're reviewing your data to determine the best way to move
          your data into Ruddr.
        </NoticeText>
      </>
    ),
    guided: (
      <>
        <NoticeIcon icon="users" />
        <NoticeText>
          <strong>Your Harvest account needs a scheduled migration.</strong> Due to the amount of data in your Harvest
          account, the Ruddr team will run this migration for you. We've already notified the team, and they'll reach
          out to schedule it and go through the next steps with you.
        </NoticeText>
      </>
    ),
    guidedUpdate: (
      <>
        <NoticeIcon icon="users" />
        <NoticeText>
          <strong>The Ruddr team runs updates for your Harvest account.</strong> Due to the amount of data in your
          Harvest account, contact Ruddr support to bring over changes from Harvest or to change the migration settings.
        </NoticeText>
      </>
    ),
  };

  return (
    <>
      <Header>
        <Title>Migration Status</Title>
        <HeaderStatus>
          {sync && (
            <LastActivity>
              Last activity: {moment(sync.endedAt ?? sync.startedAt ?? sync.createdAt).fromNow()}
            </LastActivity>
          )}
          <SyncStatus sync={sync} />
          <IconButton icon="cog" tooltip="Change Settings" disabled={guidedMigration} onClick={onChangeSettings} />
        </HeaderStatus>
      </Header>
      {!isNeverRun ? (
        <>
          <SyncStatusBar sync={sync} />
          {guidedMigration && <NoticeBlock>{notices.guidedUpdate}</NoticeBlock>}
        </>
      ) : guidedMigration ? (
        <NoticeBlock>{notices.guided}</NoticeBlock>
      ) : migratedWithoutSync ? (
        <Intro>
          <IntroIcon icon="info-circle" />
          <NoticeText>
            Your Harvest data has already been migrated. Choose Update to bring over recent changes.
          </NoticeText>
        </Intro>
      ) : results ? (
        <>
          <Intro>
            <IntroIcon icon="info-circle" />
            <NoticeText>
              <strong>How moving from Harvest works</strong>
              <p>
                We copy your data from Harvest into Ruddr: your team, clients, projects, time entries, expenses, and
                invoices. Nothing in Harvest is changed or deleted.
              </p>
              <p>
                Start the migration when you're ready. It runs in the background, so you can leave this page. The
                estimate below shows roughly how long it will take.
              </p>
              <p>
                When it's finished, look over a few projects and timesheets in Ruddr. If anything changes in Harvest
                afterward, run an incremental migration to bring over just the updates.
              </p>
            </NoticeText>
          </Intro>
          <TilesLabel>
            {integration.info.companyName
              ? `Found in ${integration.info.companyName} (${integration.info.accountId})`
              : `Found in Harvest account ${integration.info.accountId}`}
          </TilesLabel>
          <Tiles>
            <Tile>
              <TileLabel>Members</TileLabel>
              <TileValue>{harvestMembers == null ? '–' : harvestMembers.toLocaleString()}</TileValue>
            </Tile>
            <Tile>
              <TileLabel>Clients</TileLabel>
              <TileValue>{harvestClients == null ? '–' : harvestClients.toLocaleString()}</TileValue>
            </Tile>
            <Tile>
              <TileLabel>Projects</TileLabel>
              <TileValue>{harvestProjects == null ? '–' : harvestProjects.toLocaleString()}</TileValue>
            </Tile>
            <Tile>
              <TileLabel>Time Entries</TileLabel>
              <TileValue>{harvestTimeEntries == null ? '–' : harvestTimeEntries.toLocaleString()}</TileValue>
            </Tile>
            <Tile>
              <TileLabel>Invoices</TileLabel>
              <TileValue>{harvestInvoices == null ? '–' : harvestInvoices.toLocaleString()}</TileValue>
            </Tile>
          </Tiles>
        </>
      ) : (
        <NoticeBlock>{notices.loading}</NoticeBlock>
      )}

      <Actions>
        {showEstimate && (
          <Estimate>
            Estimated Time to Migrate ~{records} {recordCount === 1 ? 'record' : 'records'} is{' '}
            {duration.charAt(0).toLowerCase() + duration.slice(1)}
          </Estimate>
        )}
        {!isRunning ? (
          <Buttons align="right">
            <Button disabled={runDisabled} onClick={onNewSync}>
              {startLabel}
            </Button>
          </Buttons>
        ) : (
          <SyncProgress sync={sync} />
        )}
      </Actions>
    </>
  );
}
